import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@roger/ui-components';
import { ShieldCheck } from 'lucide-react';
import { useToast } from '../../hooks/useToastContext';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';
import {
  MIN_ESIGN_PASSWORD_LENGTH,
  changeESignatureCredential,
  createESignatureCredential,
} from './esignatureCredentialApi';
import {
  invalidateESignatureCredentialStatus,
  useESignatureCredential,
} from './useESignatureCredential';
import { ESignatureResetFields } from './ESignatureResetFields';

interface ESignaturePasswordDialogProps {
  open: boolean;
  signerEmail: string | null;
  onClose: () => void;
}

/**
 * Manage your own e-signature password, from the user menu.
 *
 * Create one if you have none, otherwise change it by proving the current one.
 * The forgot path is the same emailed-code flow the signing dialog offers.
 */
export const ESignaturePasswordDialog: React.FC<
  ESignaturePasswordDialogProps
> = ({ open, signerEmail, onClose }) => {
  const { status, isLoading, refresh, revalidate, isStale } =
    useESignatureCredential();
  const { success: showSuccess, error: showError } = useToast();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  const hasCredential = status?.has_credential ?? false;

  useEffect(() => {
    if (!open) {
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setValidationError(null);
      setIsResetting(false);
    }
  }, [open]);

  // The header mounts once for the whole page session, so without this the
  // dialog would still be in create mode after the clinician set their password
  // during a signature, and "Create password" would come back 409 with no field
  // to enter the existing one. Submitting is blocked while it is in flight,
  // because the visible fields still come from the pre-recheck status.
  const [isRechecking, setIsRechecking] = useState(false);
  useEffect(() => {
    if (!open) {
      return;
    }
    let current = true;
    setIsRechecking(true);
    void revalidate().finally(() => {
      if (current) {
        setIsRechecking(false);
      }
    });
    return () => {
      current = false;
    };
  }, [open, revalidate]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (newPassword !== confirmPassword) {
      setValidationError('Those passwords do not match.');
      return;
    }
    if (newPassword.length < MIN_ESIGN_PASSWORD_LENGTH) {
      setValidationError(
        `Use at least ${MIN_ESIGN_PASSWORD_LENGTH} characters for your e-signature password.`
      );
      return;
    }
    setValidationError(null);
    setIsSubmitting(true);
    try {
      if (hasCredential) {
        await changeESignatureCredential(currentPassword, newPassword);
      } else {
        await createESignatureCredential(newPassword);
      }
      invalidateESignatureCredentialStatus();
      await refresh();
      showSuccess(
        hasCredential
          ? 'E-signature password updated'
          : 'E-signature password created'
      );
      onClose();
    } catch (error) {
      console.error('Failed to save e-signature password', error);
      showError(
        'Could not save your e-signature password',
        getBusinessErrorMessage(error) ?? 'Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmit =
    !isSubmitting &&
    !isLoading &&
    !isRechecking &&
    !isStale &&
    newPassword.length > 0 &&
    confirmPassword.length > 0 &&
    (!hasCredential || currentPassword.length > 0);

  return (
    <Dialog open={open} onOpenChange={next => !next && onClose()}>
      <DialogContent className='sm:max-w-[480px]'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2 text-foreground'>
            <ShieldCheck className='h-5 w-5 text-primary' />
            Electronic Signature
          </DialogTitle>
          <DialogDescription className='pt-2 text-sm'>
            You enter this password each time you sign a chart. It is separate
            from your Roger sign-in password.
          </DialogDescription>
        </DialogHeader>

        {isResetting ? (
          <ESignatureResetFields
            signerEmail={signerEmail}
            onDone={async () => {
              await refresh();
              onClose();
            }}
            onCancel={() => setIsResetting(false)}
          />
        ) : (
          <form onSubmit={handleSubmit} className='space-y-4'>
            {/* Real username field so the password manager files the entry
                under this account. */}
            <input
              type='text'
              name='username'
              autoComplete='username'
              readOnly
              value={signerEmail ?? ''}
              className='sr-only'
              tabIndex={-1}
              aria-hidden='true'
            />
            {hasCredential ? (
              <div className='space-y-1.5'>
                <Label htmlFor='esign-current-password'>
                  Current e-signature password
                </Label>
                <Input
                  id='esign-current-password'
                  name='current-password'
                  type='password'
                  autoComplete='current-password'
                  autoFocus
                  value={currentPassword}
                  onChange={e => setCurrentPassword(e.target.value)}
                />
                <button
                  type='button'
                  className='text-xs text-primary hover:underline'
                  onClick={() => setIsResetting(true)}
                >
                  Forgot your e-signature password?
                </button>
              </div>
            ) : null}

            <div className='space-y-1.5'>
              <Label htmlFor='esign-manage-new-password'>
                New e-signature password
              </Label>
              <Input
                id='esign-manage-new-password'
                name='new-password'
                type='password'
                autoComplete='new-password'
                autoFocus={!hasCredential}
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
              />
            </div>
            <div className='space-y-1.5'>
              <Label htmlFor='esign-manage-confirm-password'>
                Confirm e-signature password
              </Label>
              <Input
                id='esign-manage-confirm-password'
                name='confirm-new-password'
                type='password'
                autoComplete='new-password'
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
              />
            </div>

            {isStale ? (
              <p className='text-sm text-destructive'>
                Could not confirm your e-signature setup.{' '}
                <button
                  type='button'
                  className='underline'
                  onClick={() => void refresh()}
                >
                  Try again
                </button>
                .
              </p>
            ) : null}

            {validationError ? (
              <p className='text-sm text-destructive'>{validationError}</p>
            ) : null}

            <DialogFooter>
              <Button type='button' variant='outline' onClick={onClose}>
                Cancel
              </Button>
              <Button type='submit' disabled={!canSubmit}>
                {isSubmitting
                  ? 'Saving…'
                  : hasCredential
                    ? 'Update password'
                    : 'Create password'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
